<template>
  <div class="tutorial-page">
    <div class="page-header-card">
      <div class="header-left">
        <h1 class="guide-title">
          <span class="brand-love">爱教务</span>{{ isDivision ? '智能分班系统使用指南' : (isCourseSchedule ? '智能排课系统使用指南' : '排监考系统新手指南') }}
        </h1>
        <p class="guide-subtitle">{{ isDivision ? '跟随以下 5 个步骤，轻松搞定科学合理的分班任务、规则管理与微调' : (isCourseSchedule ? '跟随以下 5 个步骤，轻松搞定全校课表编排、走班与体育专项设置及微调' : '跟随以下 5 个步骤，轻松搞定全校排监考与监考工作量统计') }}</p>
      </div>
      <el-button type="primary" @click="$router.push('/portal')">返回系统入口</el-button>
    </div>

    <!-- Core Scheduling/Division Process Flowchart -->
    <div class="flowchart-card">
      <h3 class="flowchart-title">
        <el-icon><Compass /></el-icon> {{ isDivision ? '核心分班极速上手流程' : (isCourseSchedule ? '核心排课极速上手流程' : '核心排监考极速上手流程') }}
      </h3>
      <div class="flowchart-steps">
        <div 
          v-for="(step, index) in steps" 
          :key="index" 
          class="flow-step"
          :class="{ active: activeIndex === index }"
          @click="selectStep(index)"
        >
          <div class="flow-step-circle">
            <el-icon v-if="index === 0"><List /></el-icon>
            <el-icon v-else-if="index === 1"><Calendar /></el-icon>
            <el-icon v-else-if="index === 2"><Setting /></el-icon>
            <el-icon v-else-if="index === 3"><Cpu /></el-icon>
            <el-icon v-else-if="index === 4"><Download /></el-icon>
          </div>
          <div class="flow-step-info">
            <span class="flow-step-num">STEP 0{{ index + 1 }}</span>
            <span class="flow-step-name">{{ step.title }}</span>
          </div>
          <div v-if="index < steps.length - 1" class="flow-arrow-container">
            <div class="flow-arrow-line"></div>
            <el-icon class="flow-arrow-icon"><ArrowRight /></el-icon>
          </div>
        </div>
      </div>
    </div>

    <div class="tutorial-layout">
      <!-- Left Vertical Step Menu & Contact Support -->
      <div class="left-aside">
        <div class="tutorial-sidebar">
          <div 
            v-for="(step, index) in steps" 
            :key="index" 
            class="sidebar-step-item"
            :class="{ active: activeIndex === index }"
            @click="selectStep(index)"
          >
            <div class="step-num">{{ index + 1 }}</div>
            <div class="step-meta">
              <span class="step-label">步骤 {{ index + 1 }}</span>
              <span class="step-title-text">{{ step.title }}</span>
            </div>
          </div>
        </div>

        <!-- Contact Support Card -->
        <div class="contact-support-card">
          <h3 class="support-title">💬 帮助与技术支持</h3>
          <p class="support-desc">如果您在使用过程中遇到任何问题，欢迎随时联系我们：</p>
          
          <div class="contact-info-list">
            <div class="contact-info-item">
              <span class="info-label">QQ客服</span>
              <span class="info-value">1072677403</span>
              <el-button 
                type="primary" 
                link 
                size="small" 
                @click="copyQQ"
                class="copy-btn"
              >
                复制
              </el-button>
            </div>

            <div class="contact-info-item wechat-qr-section">
              <span class="info-label">微信二维码</span>
              <div class="qr-code-box">
                <el-image 
                  v-if="!qrLoadFailed"
                  src="/img/tutorial/wechat_qr.png" 
                  @error="qrLoadFailed = true"
                  class="qr-code-img"
                  alt="微信客服二维码"
                  :preview-src-list="['/img/tutorial/wechat_qr.png']"
                  preview-teleported
                  fit="contain"
                />
                <div v-else class="qr-code-placeholder">
                  <el-icon :size="24" class="qr-placeholder-icon"><Picture /></el-icon>
                  <span class="qr-placeholder-text">微信二维码</span>
                  <span class="qr-placeholder-status">待上传</span>
                </div>
              </div>
              <p v-if="qrLoadFailed" class="qr-upload-hint">
                管理员可在路径：<br />
                <code>public/img/tutorial/wechat_qr.png</code><br />
                放置微信二维码图片进行替换展示。
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Step Content Detail (with Carousel for multiple screenshots) -->
      <div class="tutorial-content">
        <el-card class="content-card">
          <div class="step-header">
            <h2>{{ activeStep.title }}</h2>
            <p class="step-desc">{{ activeStep.description }}</p>
          </div>

          <!-- Instructions (Full Width) -->
          <div class="step-instructions">
            <h3>💡 核心操作要点</h3>
            <ul class="points-grid">
              <li v-for="(point, pIdx) in activeStep.points" :key="pIdx">
                <span class="point-num">{{ pIdx + 1 }}</span>
                <span>{{ point }}</span>
              </li>
            </ul>

            <div class="tip-box" v-if="activeStep.tip">
              <el-icon class="tip-icon"><InfoFilled /></el-icon>
              <div class="tip-content">
                <strong>操作提示：</strong>
                <span>{{ activeStep.tip }}</span>
              </div>
            </div>
          </div>

          <!-- Large Media Section (Full Width) -->
          <div class="media-container large-media">
            <div class="carousel-header">
              <el-icon><Picture /></el-icon>
              <span>操作示意图 (左右滑动查看多张，点击图片可放大预览)</span>
            </div>
            
            <el-carousel 
              :autoplay="false" 
              arrow="always" 
              indicator-position="outside" 
              class="tutorial-carousel"
              ref="carouselRef"
            >
              <el-carousel-item 
                v-for="(img, imgIdx) in activeStep.images" 
                :key="imgIdx"
              >
                <div class="image-wrapper large-wrapper">
                  <el-image 
                    v-if="!failedImages[`${activeIndex}_${imgIdx}`]"
                    :src="img.path" 
                    :preview-src-list="activeStep.images.map(item => item.path)"
                    :initial-index="imgIdx"
                    fit="contain"
                    @error="handleImageError(activeIndex, imgIdx)"
                    class="tutorial-image large-image"
                    :alt="img.label"
                    preview-teleported
                  />
                  <div v-else class="image-placeholder">
                    <el-icon :size="48" class="placeholder-icon"><Picture /></el-icon>
                    <p class="placeholder-title">{{ img.label }}</p>
                    <p class="placeholder-hint">
                      管理员可在路径：<br />
                      <code>public{{ img.path }}</code><br />
                      放置截图文件，此处即可自动显示。
                    </p>
                  </div>
                </div>
                <div class="image-caption">{{ imgIdx + 1 }}. {{ img.label }}</div>
              </el-carousel-item>
            </el-carousel>
          </div>

          <div class="faq-section">
            <div class="faq-header">
              <h3>常见问题 Q&A</h3>
              <span>遇到问题、设置不生效时，先看这里</span>
            </div>
            <el-collapse v-model="activeFaqs" class="faq-collapse">
              <el-collapse-item
                v-for="(faq, faqIdx) in faqs"
                :key="faqIdx"
                :name="String(faqIdx)"
              >
                <template #title>
                  <span class="faq-question">{{ faq.question }}</span>
                </template>
                <p class="faq-answer">{{ faq.answer }}</p>
              </el-collapse-item>
            </el-collapse>
          </div>

          <div class="step-footer">
            <el-button 
              :disabled="activeIndex === 0" 
              @click="prevStep"
              icon="ArrowLeft"
            >
              上一步
            </el-button>
            <el-button 
              v-if="activeIndex < steps.length - 1" 
              type="primary" 
              @click="nextStep"
            >
              下一步 <el-icon class="el-icon--right"><ArrowRight /></el-icon>
            </el-button>
            <el-button 
              v-else 
              type="success" 
              @click="finishTutorial"
              icon="Check"
            >
              完成新手引导
            </el-button>
          </div>
        </el-card>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { 
  InfoFilled, 
  Picture,
  ArrowRight,
  List,
  Calendar,
  Setting,
  Cpu,
  Download,
  Compass
} from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'

const route = useRoute()
const router = useRouter()
const activeIndex = ref(0)
const qrLoadFailed = ref(false)
const failedImages = ref({})
const carouselRef = ref(null)
const activeFaqs = ref(['0'])

const isDivision = computed(() => route.query.type === 'division')
const isCourseSchedule = computed(() => route.query.type === 'course-schedule')

const scheduleSteps = [
  {
    title: '准备基础数据',
    description: '在排考之前，需要将学校的基本教务数据录入或导入系统。',
    points: [
      '在“学期管理”中，新建当前需要排考的学期，并设定参与排考的年级范围。',
      '在“教师管理”中录入或批量导入教师名单，快速初始化全校教师库。',
      '进入具体教师的编辑面板，完善任教科目、任教年级、最大监考时长等信息。'
    ],
    images: [
      { path: '/img/tutorial/step1_1.png', label: '新建学期与设定年级范围' },
      { path: '/img/tutorial/step1_2.png', label: '教师数据导入与任教科目设置' },
      { path: '/img/tutorial/step1_3.png', label: '编辑教师详细属性与监考偏好' }
    ],
    tip: '导入教师时，姓名和学科为必填项；任教科目越完整，系统越容易自动避开本学科教师监考。'
  },
  {
    title: '创建考试及科目',
    description: '定义具体的考试，以及每门科目详细的开考时间与考场容量。',
    points: [
      '在“考试管理”中，点击“创建考试”，填写考试名称及相关描述。',
      '点击“科目设置”，逐一添加科目，配置准确的考试日期、开始时间、结束时间和考场信息。',
      '【3+1+2选考设置】：对于同一时间段同时进行的考试（如物理/历史同时考，或化学/政治重合考），请为每门科目分别新建一个场次，并设为相同的时间段。系统会自动进行冲突检查，避免教师分身监考。',
      '【连续不换班设置】：如果多门选考科目是连续考、中途不更换监考老师，可以在系统中直接创建一个大时间段场次（如命名为“选科综合(4选2)”），时间设为多科目的总时长即可。'
    ],
    images: [
      { path: '/img/tutorial/step2_1.png', label: '创建考试名称与设置' },
      { path: '/img/tutorial/step2_2.png', label: '添加考试科目与开考时间' }
    ],
    tip: '相同或重合的考试时间段是支持 3+1+2 选科排考的关键；系统会按时间段自动校验教师冲突。'
  },
  {
    title: '设置智能排考规则',
    description: '根据学校的监考管理要求，针对特定的考试项目灵活配置算法约束条件。',
    points: [
      '在“考试管理”中设置监考规则，可选择“完全隔离”“交叉监考”“完全混合”或“自定义分组”。',
      '在排考页面可勾选“平衡晚场”：系统会在均衡时长的同时，尽量均衡 17:00 及以后场次的监考次数。',
      '若个别教师在本次考试中存在特殊时间冲突，可在排考页面点击“设置监考限制”进行设置，设置后需要重新点击“一键智能排考”。'
    ],
    images: [
      { path: '/img/tutorial/step3_1.png', label: '全局统排与考场分配方式' },
      { path: '/img/tutorial/step3_2.png', label: '设置教师在本次考试下的不可监考时段' }
    ],
    tip: '开启“平衡晚场”后，工作量统计会显示尾场次数，方便检查晚场安排是否均衡。'
  },
  {
    title: '一键自动排考',
    description: '依靠智能排考算法，自动计算并输出尽量均衡、无时间冲突 of 监考方案。',
    points: [
      '在排考页面，确认规则无误后，点击“一键智能排考”。',
      '算法运行成功后，将自动填充每个考场的监考人员，并综合考虑任教学科、年级规则、工作量均衡和教师个人限制。',
      '如需微调，可点击单元格选择其他空闲教师，或使用“手动调换”快速互换两个监考安排。'
    ],
    images: [
      { path: '/img/tutorial/step4_1.png', label: '一键运行智能自动排考' },
      { path: '/img/tutorial/step4_2.png', label: '可视化大表手动点击调换监考' }
    ],
    tip: '手动调整时，系统会自动校验教师是否在同一时间已有其他安排，避免产生时间冲突。'
  },
  {
    title: '确认并导出数据',
    description: '查看全校监考大表，确认无误后一键下载为 Excel 文件。',
    points: [
      '在“监考大表”视图下，可查看横轴为场次、纵轴为考场的全景排考视图。',
      '在“工作量统计”卡片中，可查看每位老师的监考场次、总时长、尾场次数等统计信息；巡考时长根据排考折算系数计入。',
      '点击“导出 Excel”即可导出监考表，方便打印张贴或分发到教师群中。'
    ],
    images: [
      { path: '/img/tutorial/step5_1.png', label: '监考大表与调换结果预览' },
      { path: '/img/tutorial/step5_2.png', label: '监考工作量统计（含尾场）与导出' }
    ],
    tip: '手动微调后，工作量统计会自动刷新；导出前建议重点检查最大差距、空白单元格 and 晚场次数。'
  }
]

const scheduleFaqs = [
  {
    question: 'Q1：点击“一键智能排考”后排不出来，通常是什么原因？',
    answer: '优先检查可用教师是否足够、是否设置了过多“不监考”限制、教师最大监考时长是否过低，以及“完全隔离/交叉监考”规则下某个年级教师数量是否不足。可以先放宽限制或改用“完全混合/自定义分组”再重试。'
  },
  {
    question: 'Q2：刚设置了“整场不监考”或“特定场次不监考”，为什么表格没有变化？',
    answer: '监考限制属于排考约束，修改后需要重新点击“一键智能排考”才会重新生成安排。已经手动改过的结果不会在设置限制后自动重排。'
  },
  {
    question: 'Q3：3+1+2 选科同时考试应该怎么设置？',
    answer: '同一时间段同时进行的科目建议分别创建场次，并设置相同或重合的日期、开始时间和结束时间。系统会按时间段校验教师冲突，避免同一教师同时出现在多个考场。'
  },
  {
    question: 'Q4：巡考老师会计入监考工作量吗？',
    answer: '默认不计入。但可以在排考设置中配置“巡考工作量折算系数”，系统会按系数折算并计入教师监考总时长中。'
  },
  {
    question: 'Q5：自动排完后还能手动调整吗？',
    answer: '可以。可以点击具体监考单元格选择其他教师，也可以使用“手动调换”依次点击两个监考单元格完成互换。系统会在调整时继续做时间冲突校验。'
  }
]

const divisionSteps = [
  {
    title: '导入学生数据与分数',
    description: '在新建的分班工作区内，下载 Excel 导入模板，填入学生的基础数据并一键导入。',
    points: [
      '进入您的分班工作区，点击“数据导入”按钮，下载官方标准的 Excel 模板。',
      '填写学生的学号、姓名、性别、各科分数及“特色标签”（如特长生、团员等）。',
      '支持 unrecognized 性别校验，性别应填“男”或“女”；旧的 `.xls` 格式必须另存为 `.xlsx` 后上传。',
      '选择“覆盖导入”（清空当前列表重新导入）或“追加导入”（增量更新学生名册）。'
    ],
    images: [
      { path: '/img/tutorial/division_step1_1.png', label: '下载分班 Excel 模板与导入' },
      { path: '/img/tutorial/division_step1_2.png', label: '导入跳过及异常格式行警告提示' }
    ],
    tip: '导入时，对于格式不符合要求的行，系统会自动提供跳过警告。若有大批跳过，请检查性别格式或文件扩展名。'
  },
  {
    title: '配置同班与互斥规则',
    description: '设置特定学生之间的组合或规避规则，确保分班结果符合学校管理要求。',
    points: [
      '同班规则：可将双胞胎或需要特殊照顾的两个或多个学生强制分到同一个班级。',
      '互斥规则：可设定具有矛盾或不宜在一起的若干名学生绝对分在不同的班级。',
      '预分/排除规则：强制指定某学生去往某个班级（预分班），或绝对不能去往某班（排除）。',
      '冲突仲裁：预分班与同班规则的优先级高于互斥和排除，后端会自动做出冲突仲裁。'
    ],
    images: [
      { path: '/img/tutorial/division_step2_1.png', label: '设置学生同班、互斥、预分班等多样化规则' },
      { path: '/img/tutorial/division_step2_2.png', label: '导入/导出规则 JSON 配置文件' }
    ],
    tip: '规则设定完毕后会自动保存。若学生姓名填错，系统在启动算法时会进行智能冲突校验。'
  },
  {
    title: '设定班级层级与人数',
    description: '设置普通平行分班或树状分层分班，并在各班配置目标招生人数。',
    points: [
      '普通平行分班：所有班级均等分配，自动均衡男女比例、总分均分与单科成绩。',
      '树状分层分班：您可以自定义多个层级（如强基班、实验班、平行班），根据总分或科目名次/分数段提取学生。',
      '层级提取名次数：填写的“层级提取名次数 (N人)”是指此层级实际招收人数（非累计名次）。例如：层级1招前 50 名，层级2招接下来的 50 名，此处应填 50，而非 100。',
      '每班目标人数：在各层级下，您可以针对不同的班级单独设置人数上限；若留空，系统将自动平均分配。'
    ],
    images: [
      { path: '/img/tutorial/division_step3_1.png', label: '配置强基班、实验班、平行班等多级树状分层' },
      { path: '/img/tutorial/division_step3_2.png', label: '自定义设置每个班级的目标分配人数上限' }
    ],
    tip: '层级筛选人数是指当前层级额外提取的人数。请仔细阅读层级底部的黄色警告文字说明。'
  },
  {
    title: '启动智能分班算法',
    description: '依靠高并发退火与遗传智能优化算法，一键计算输出最优的分班方案。',
    points: [
      '在工作区首页，确认规则无误后，点击“立即启动智能分班”以开启后台运算任务。',
      '系统在后台使用专有的 tenant_session_scope 机制执行，与 HTTP 请求完全解耦，可规避生命周期风险。',
      '页面上方会实时展示运算进度 and 后台日志，支持刷新或离开页面，任务依然会在服务器后台安全运行。',
      '算法会全自动实现各个班级间男女比例的绝对平衡，以及总分和各单科平均分的极小化方差。'
    ],
    images: [
      { path: '/img/tutorial/division_step4_1.png', label: '点击立即启动智能分班算法' },
      { path: '/img/tutorial/division_step4_2.png', label: '控制台与进度条日志实时监控' }
    ],
    tip: '在计算期间，可以随时查看右侧监控栏中的具体进度百分比及算法每一步的优化状态。'
  },
  {
    title: '微调、大屏与结果导出',
    description: '通过智能推荐进行微调，预览详细名册，并在确认后导出 Excel 报表。',
    points: [
      '手动调班与智能推荐：可任意选择两个班级的学生进行交换，系统会自动推荐使得平均分波幅最小的最佳交换人选。锁定状态或带有预分班规则的学生会被安全拦截。',
      '分班花名册预览：提供 Table 详细表格视图与 Card 看板分栏视图，支持按姓名快速过滤检索，被锁定的学生可一键解锁或加锁。',
      '阳光分班投屏大屏：支持大屏幕实时实况分班滚动投屏，提升分班的公开性、透明度与仪式感。',
      '一键导出 Excel：自动生成年级总表和各班级独立工作表，显示每位学生的分数，并在每张表的最底部自动生成加粗均分行，采用中文文件名。'
    ],
    images: [
      { path: '/img/tutorial/division_step5_1.png', label: '手动微调与智能推荐交换人选' },
      { path: '/img/tutorial/division_step5_2.png', label: '花名册卡片/表格视图与大屏投屏显示' }
    ],
    tip: '导出的 Excel 已做 Bearer Token 的 Axios 下载安全处理，避免产生 401 权限问题。'
  }
]

const divisionFaqs = [
  {
    question: 'Q1：为什么提示规则冲突？如何排查？',
    answer: '通常是因为同一个学生被设置了互相矛盾的硬性限制（例如：同一个学生预分到了两个不同的班级）。系统会自动仲裁软性冲突（如：同班且互斥，或者预分且排除，系统以预分和同班为最高优先级），但对于物理上无法成立的硬冲突（如预分到两个班）会提示错误，请在“规则设定”中修改。'
  },
  {
    question: 'Q2：树状分层分班时，每个班级的人数怎么设置？',
    answer: '在开启分层后，每个层级的卡片展开后会显示“每班目标人数”配置面板。您可以为该层级内的班级单独输入具体的人数限制。如果留空，系统将自动平均分配剩余学生。'
  },
  {
    question: 'Q3：为什么分班进度卡在 81% 或显示已完成但看不见人？',
    answer: '请刷新页面或等待后台任务执行完毕。若导入的学生性别不合规范（如填了“未知”或留空），系统会自动记录在跳过/警告日志中，可在日志中查看。'
  },
  {
    question: 'Q4：微调交换时，系统提示“交换失败”是什么原因？',
    answer: '为了保证数据严肃性，系统禁止交换状态为“已锁定”的学生。同时，如果交换会违反某位学生的“预分班”硬性规则（即该学生必须在某班），交换也会被安全拦截。'
  },
  {
    question: 'Q5：导出的 Excel 文件里，均分是怎么计算的？',
    answer: '导出的 Excel 会自动为年级总表以及每个班级的独立工作表生成最后一行“均分”。该行会提取当前工作表内所有学生的所有有效单科成绩，自动求和并计算平均值，以粗体显示在对应科目下方。'
  }
]

const courseScheduleSteps = [
  {
    title: '新建排课与时间设置',
    description: '创建本次排课任务并配置作息结构、每周天数及公共时段。',
    points: [
      '在“排课系统”主页，点击“新建排课”，填入名称并选择关联学期。',
      '可选择内置的省份/学段预设模板（如“广东高考3+1+2模式”），一键继承相应的科目及排课约束规则。',
      '设置每周排课天数、每天课节数（上午/下午/晚上），支持按天微调课节数。',
      '进入工作区后，根据学校实际需要，配置早读、眼保健操、午休等“公共时段”。'
    ],
    images: [
      { path: '/img/tutorial/schedule_step1_1.png', label: '新建排课与学段模式预设选择' },
      { path: '/img/tutorial/schedule_step1_2.png', label: '配置每天上午/下午/晚上的课节数' },
      { path: '/img/tutorial/schedule_step1_3.png', label: '配置公共时段（如早读、眼保健操等）' }
    ],
    tip: '新建排课时若选择了“新高考/省份预设”，后续设置中会自动拦截非法的高考选科组合（例如3+1+2中物理与历史冲突）。'
  },
  {
    title: '走班与体育合班设置',
    description: '根据教学特色需求，配置体育专项课的大合班方案或新高考模式下的选科走班拼盘。',
    points: [
      '如果启用了走班与体育合班功能，进入工作区后第一步便是进入“走班与体育合班设置”向导。',
      '【体育合班计划】：设定多个必须在同一时间上体育课的行政班（如高一1-2班），设置不同的体育专项教师，实现大模块合并排课。',
      '【走班合班计划】：导入走班教学班名单，设定年级走班的统一时段，系统将在排课时自动锁死相关时段，保证行政班与走班时间物理隔离。',
      '在此配置好后，走班与体育合班计划会自动同步至后续步骤的课时及教师安排中，防止后续产生时间重叠。'
    ],
    images: [
      { path: '/img/tutorial/schedule_step2_1.png', label: '体育专项合班拼盘规则向导' },
      { path: '/img/tutorial/schedule_step2_2.png', label: '走班合班计划虚拟班级与教师绑定' }
    ],
    tip: '建议首先配置走班和合班计划，这样在输入常规课程课时的时候，系统就能自动扣减和避让，显著降低冲突率。'
  },
  {
    title: '完善基础信息与任课安排',
    description: '通过分步填写向导，准备好科目库、教师库、班级设置，并安排每周各班的课时及任课老师。',
    points: [
      '【科目与教师】：在第一步“科目设置”中设置每个科目是否需要专用场地、是否允许连堂；在第二步“教师设置”中添加教师并设定其任教科目。',
      '【年级与班级】：在第三步中创建排课年级并设定班级列表。您可以使用“生成1-10班”功能一键批量建班，并为班级关联所开的学科。',
      '【每周课时与任课教师】：在第四、五步中，为每个班级的每门课输入每周上课节数（如语文5节），并下拉选择任教老师。'
    ],
    images: [
      { path: '/img/tutorial/schedule_step3_1.png', label: '教师库管理与 Excel 批量导入教师' },
      { path: '/img/tutorial/schedule_step3_2.png', label: '批量生成年级班级与设置班级类型' },
      { path: '/img/tutorial/schedule_step3_3.png', label: '每周课时数及班级任教老师绑定' }
    ],
    tip: '如果想提高效率，可以在“教师设置”中点击“批量导入”，下载 Excel 模板把全校教师和任教科目一次性导入系统。'
  },
  {
    title: '配置排课规则与个性化约束',
    description: '针对学校管理的精细化要求，为排课算法配置硬约束和软约束。',
    points: [
      '【教师不可排时段】：进入“规则约束设置”的“教师时间限制”面板，可以给特定教师设置某些节次绝对不能排课（如教研活动、半天产假）。',
      '【课程连堂规则】：设置某门课程是否必须连堂（如语文作文连堂排课）或允许在一天内排多次。',
      '【个性化高级限制】：支持配置“教师互斥”（两个老师不能同时上课）、“课程互斥”（某两门科目不能在同一天）、以及“同堂规则”（多个班合并上课）。'
    ],
    images: [
      { path: '/img/tutorial/schedule_step4_1.png', label: '设置教师在特定课节的不可排课限制' },
      { path: '/img/tutorial/schedule_step4_2.png', label: '配置科目连堂（连排）与多班同堂上课限制' }
    ],
    tip: '硬约束（如教师/场地冲突）是算法绝对要满足的；软约束（如课时均匀分布）则是尽量满足，可在排课时根据需要设定优先级权重。'
  },
  {
    title: '一键排课、沙盒推演与导出',
    description: '启动智能排课算法，生成课表并使用沙盒模式进行可视化拖拽调整，最后导出精美课表。',
    points: [
      '在“生成课表”面板中，确认规则无误后，点击“立即启动智能排课”，可在控制台实时查看运算进度与优化日志。',
      '排课完成后，可在“查看课表”中按行政班、教师、场地三个维度查看全景课表。',
      '【沙盒推演】：开启“沙盒推演模式”进行临时草稿修改。拖拽课程时，系统会自动进行冲突校验，灰色格子代表有冲突，确认满意后一键“合并至正式版”。',
      '点击“导出 Excel”或“导出 Word”，即可一键生成全校总课表、行政班分课表及教师个人课表，方便打印与分发。'
    ],
    images: [
      { path: '/img/tutorial/schedule_step5_1.png', label: '一键智能排课与控制台实时状态监控' },
      { path: '/img/tutorial/schedule_step5_2.png', label: '拖拽式排课调整与实时跨域冲突检测' },
      { path: '/img/tutorial/schedule_step5_3.png', label: '课表预览及多格式一键导出（Excel/Word）' }
    ],
    tip: '如果在排课时遇到死锁，算法会启动死局降级引擎，自动放宽软约束并输出自然语言级别的诊断报告（例如“张三老师时间冲突”），绝不会崩溃。'
  }
]

const courseScheduleFaqs = [
  {
    question: 'Q1：体育合班（体育专项课）和普通的常规课排课有什么区别？',
    answer: '普通课程是一个教师对一个班级。而体育合班是“多班同排拆分模式”，即多个行政班（例如：高一1班和2班）必须在同一个时间段上体育课。学生按选报的专项（篮球、排球等）打散重组为专项教学班，由不同老师授课。排课引擎在排课时，会把这些专项班打包在同一个时间片（Time Slot）同步排定，且自动锁死相应行政班的这一时段。'
  },
  {
    question: 'Q2：走班课和行政班课程在时间段上如何避免冲突？',
    answer: '系统支持设定“全年级走班统一时段”。在该时段内，涉及走班的常规行政班会被自动排空或仅安排自习，引擎会将走班课（如物理/历史等选科走班）全部集中在该统一时段内排定，从而保证学生不会分身乏术。'
  },
  {
    question: 'Q3：什么是“沙盒推演模式”？修改了会直接影响正式课表吗？',
    answer: '不会。沙盒模式是系统提供的一种“安全演练”环境。开启后，你对课表的任意拖拽调整、重新编排都只保存在临时沙盒中。只有当你点击“发布合并至正式版”时，修改才会真正覆盖正式课表。若不满意，随时可以点击“放弃修改”彻底还原。'
  },
  {
    question: 'Q4：手动拖拽调整课表时，如何知道哪些格子可以放？',
    answer: '当你开始拖拽某节课时，课表上其他的格子会呈现不同状态。绿色格子表示无冲突且推荐调换；灰色背景 of 格子表示拖到该位置会引发教师冲突或班级冲突，系统也会弹出自然语言冲突警告（例如：“若将此课调至周四第2节，将导致李明同学时间冲突”）。'
  },
  {
    question: 'Q5：排课算法显示“未排满课”或“遇到死局”，该如何处理？',
    answer: '这通常是由于硬约束过紧导致的（例如：给教师设置了太多的不可排时间、某个特定班级的排课天数不足以容纳其课时、或者某科目的连堂规则与其他科目严重冲突）。此时可以前往“规则约束设置”放宽部分限制，或在教师时间里减少限制，再重新一键排课。'
  }
]

const steps = computed(() => {
  if (isDivision.value) return divisionSteps
  if (isCourseSchedule.value) return courseScheduleSteps
  return scheduleSteps
})

const faqs = computed(() => {
  if (isDivision.value) return divisionFaqs
  if (isCourseSchedule.value) return courseScheduleFaqs
  return scheduleFaqs
})

const activeStep = computed(() => steps.value[activeIndex.value])

watch(activeIndex, () => {
  if (carouselRef.value) {
    carouselRef.value.setActiveItem(0)
  }
})

watch(() => route.query.type, () => {
  activeIndex.value = 0
})

const selectStep = (index) => {
  activeIndex.value = index
}

const prevStep = () => {
  if (activeIndex.value > 0) {
    activeIndex.value--
  }
}

const nextStep = () => {
  if (activeIndex.value < steps.value.length - 1) {
    activeIndex.value++
  }
}

const handleImageError = (stepIdx, imgIdx) => {
  failedImages.value[`${stepIdx}_${imgIdx}`] = true
}

const copyQQ = () => {
  navigator.clipboard.writeText('1072677403')
  ElMessage.success('QQ号码 1072677403 已成功复制到剪贴板！')
}

const finishTutorial = () => {
  localStorage.setItem('tutorial_dismissed', 'true')
  let successMsg = '恭喜，您已完成排监考系统新手引导！'
  if (isDivision.value) {
    successMsg = '恭喜，您已完成智能分班系统新手引导！'
  } else if (isCourseSchedule.value) {
    successMsg = '恭喜，您已完成智能排课系统新手引导！'
  }
  ElMessage.success(successMsg)
  router.push('/portal')
}
</script>

<style scoped>
.tutorial-page {
  padding: 24px;
  max-width: 1280px;
  margin: 0 auto;
  min-height: 100vh;
  background-color: #f8fafc;
}

.page-header-card {
  background: #ffffff;
  padding: 24px 32px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  border: 1px solid #f1f5f9;
}

.guide-title {
  font-size: 24px;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 6px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.brand-love {
  background: linear-gradient(135deg, #ff4e50 0%, #f9d423 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.guide-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.tutorial-layout {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

.left-aside {
  width: 320px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Sidebar Navigation */
.tutorial-sidebar {
  background: #ffffff;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
  border: 1px solid #f1f5f9;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sidebar-step-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.25s ease;
  border: 1px solid transparent;
}

.sidebar-step-item:hover {
  background-color: #f1f5f9;
}

.sidebar-step-item.active {
  background-color: #f0fdf4;
  border-color: #bbf7d0;
}

.step-num {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #e2e8f0;
  color: #64748b;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: all 0.25s ease;
}

.sidebar-step-item.active .step-num {
  background-color: #22c55e;
  color: #ffffff;
}

.step-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.step-label {
  font-size: 11px;
  color: #94a3b8;
  font-weight: 600;
  text-transform: uppercase;
}

.step-title-text {
  font-size: 14px;
  color: #334155;
  font-weight: 600;
}

.sidebar-step-item.active .step-title-text {
  color: #15803d;
}

/* Contact Support Card */
.contact-support-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
  border: 1px solid #f1f5f9;
}

.support-title {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
}

.support-desc {
  font-size: 12px;
  color: #64748b;
  margin: 0 0 16px 0;
  line-height: 1.5;
}

.contact-info-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.contact-info-item {
  background: #f8fafc;
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  border: 1px solid #f1f5f9;
}

.info-label {
  color: #64748b;
  font-weight: 500;
}

.info-value {
  color: #334155;
  font-weight: 600;
  font-family: Consolas, Monaco, monospace;
}

.copy-btn {
  font-weight: 600;
}

.wechat-qr-section {
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.qr-code-box {
  width: 100%;
  height: 280px;
  background-color: #ffffff;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 4px;
  box-sizing: border-box;
}

.qr-code-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  cursor: pointer;
}

.qr-code-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  gap: 4px;
}

.qr-placeholder-icon {
  color: #cbd5e1;
}

.qr-placeholder-text {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.qr-placeholder-status {
  font-size: 10px;
  background: #e2e8f0;
  padding: 2px 6px;
  border-radius: 10px;
  color: #64748b;
  font-weight: 600;
}

.qr-upload-hint {
  font-size: 10px;
  color: #94a3b8;
  margin: 0;
  line-height: 1.4;
  word-break: break-all;
  font-family: Consolas, Monaco, monospace;
}

.qr-upload-hint code {
  color: #ef4444;
  font-weight: bold;
}

/* Content Area */
.tutorial-content {
  flex-grow: 1;
  width: 0;
}

.content-card {
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
  border: 1px solid #f1f5f9;
  padding: 12px;
}

.step-header {
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 20px;
  margin-bottom: 24px;
}

.step-header h2 {
  font-size: 22px;
  color: #1e293b;
  margin: 0 0 8px 0;
}

.step-desc {
  font-size: 15px;
  color: #64748b;
  margin: 0;
  line-height: 1.6;
}

.step-instructions h3 {
  font-size: 16px;
  color: #334155;
  margin-top: 0;
  margin-bottom: 16px;
}

.points-grid {
  list-style: none;
  padding: 0;
  margin: 0 0 24px 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}

.points-grid li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  color: #475569;
  line-height: 1.5;
}

.point-num {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #22c55e;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  line-height: 22px;
  text-align: center;
  flex-shrink: 0;
  margin-top: 0;
}

.tip-box {
  background-color: #eff6ff;
  border-left: 4px solid #3b82f6;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.tip-icon {
  color: #3b82f6;
  font-size: 20px;
  flex-shrink: 0;
  margin-top: 2px;
}

.tip-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: #1e40af;
  line-height: 1.5;
}

/* Media Box & Carousel */
.media-container {
  display: flex;
  flex-direction: column;
  background-color: #f8fafc;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #f1f5f9;
}

.carousel-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #64748b;
  margin-bottom: 12px;
  font-weight: 600;
}

.tutorial-carousel {
  width: 100%;
}

:deep(.el-carousel__container) {
  height: 580px !important;
}

.image-wrapper {
  width: 100%;
  height: 300px;
  border-radius: 10px;
  overflow: hidden;
  background-color: #ffffff;
  border: 1px dashed #cbd5e1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  box-sizing: border-box;
}

.large-wrapper {
  height: 540px !important;
}

.tutorial-image {
  width: 100%;
  height: 100%;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.image-placeholder {
  text-align: center;
  color: #94a3b8;
  max-width: 100%;
  padding: 12px;
  box-sizing: border-box;
}

.placeholder-icon {
  margin-bottom: 8px;
  color: #cbd5e1;
}

.placeholder-title {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  margin: 0 0 6px 0;
}

.placeholder-hint {
  font-size: 10.5px;
  line-height: 1.5;
  margin: 0;
  color: #94a3b8;
  text-align: left;
  background: #f1f5f9;
  padding: 8px 10px;
  border-radius: 6px;
  font-family: Consolas, Monaco, monospace;
}

.placeholder-hint code {
  color: #ef4444;
  font-weight: bold;
}

.image-caption {
  text-align: center;
  font-size: 12px;
  color: #64748b;
  margin-top: 10px;
  font-weight: 600;
}

.faq-section {
  margin-top: 24px;
  padding: 18px 20px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

.faq-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 12px;
}

.faq-header h3 {
  margin: 0;
  font-size: 16px;
  color: #1e293b;
}

.faq-header span {
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
  text-align: right;
}

.faq-collapse {
  border-top: 1px solid #e2e8f0;
  border-bottom: none;
}

.faq-question {
  font-size: 14px;
  font-weight: 700;
  color: #334155;
  line-height: 1.4;
}

.faq-answer {
  margin: 0;
  font-size: 13px;
  color: #475569;
  line-height: 1.7;
}

:deep(.faq-collapse .el-collapse-item__header) {
  min-height: 48px;
  height: auto;
  padding: 8px 0;
}

:deep(.faq-collapse .el-collapse-item__content) {
  padding-bottom: 16px;
}

/* Footer Navigation buttons */
.step-footer {
  margin-top: 24px;
  border-top: 1px solid #f1f5f9;
  padding-top: 20px;
  display: flex;
  justify-content: space-between;
}

@media (max-width: 992px) {
  .tutorial-layout {
    flex-direction: column;
  }
  .left-aside {
    width: 100%;
  }
  .tutorial-sidebar {
    width: 100%;
    flex-direction: row;
    overflow-x: auto;
  }
  .sidebar-step-item {
    flex-shrink: 0;
  }
  .media-container {
    margin-top: 24px;
  }
  .faq-header {
    flex-direction: column;
    align-items: flex-start;
  }
  .faq-header span {
    text-align: left;
  }
}

/* Flowchart styling */
.flowchart-card {
  background: #ffffff;
  padding: 20px 24px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
  border: 1px solid #f1f5f9;
  margin-bottom: 24px;
}

.flowchart-title {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.flowchart-title .el-icon {
  color: #ff4e50;
  font-size: 18px;
}

.flowchart-steps {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.flow-step {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #f1f5f9;
  cursor: pointer;
  transition: all 0.25s ease;
  position: relative;
}

.flow-step:hover {
  background: #f1f5f9;
  transform: translateY(-2px);
}

.flow-step.active {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border-color: #bbf7d0;
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.08);
}

.flow-step-circle {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #e2e8f0;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
  transition: all 0.25s ease;
}

.flow-step.active .flow-step-circle {
  background: #22c55e;
  color: #ffffff;
}

.flow-step-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.flow-step-num {
  font-size: 9px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.5px;
}

.flow-step-name {
  font-size: 13px;
  font-weight: 700;
  color: #334155;
}

.flow-step.active .flow-step-name {
  color: #15803d;
}

.flow-arrow-container {
  display: flex;
  align-items: center;
  position: absolute;
  right: -24px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  z-index: 2;
  color: #cbd5e1;
}

.flow-arrow-line {
  flex-grow: 1;
  height: 2px;
  background: #e2e8f0;
}

.flow-arrow-icon {
  font-size: 12px;
}

@media (max-width: 992px) {
  .flowchart-steps {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
  .flow-arrow-container {
    display: none;
  }
}
</style>
